import type { BookingResponse } from '@/shared/api';
import { stayDate } from './helpers';
import type { BookingDetailTab } from './bookingDetailTabModel';
import type { InvoiceStepState } from './bookingInvoiceModel';

export interface OverviewTodo {
  title: string;
  sub: string;
  /** Wohin der Knopf führt; ohne Ziel steht die Aufgabe nur als Hinweis da. */
  go: { label: string; tab: BookingDetailTab } | { label: string; propertyId: string } | null;
  /** Bernstein: wartet auf jemanden; sonst ein ruhiger nächster Schritt. */
  urgent: boolean;
}

/** Was der Server bei „Daten prüfen“ meldet – nur das, keine Vermutung. */
function dataQualityTodo(booking: BookingResponse): OverviewTodo {
  const code = booking.stayPhaseDataQualityCode;
  if (code === 'MissingPropertyTimezone' || code === 'InvalidPropertyTimezone') {
    return {
      title: 'Zeitzone des Apartments fehlt oder ist ungültig',
      sub: 'Ohne Zeitzone lassen sich heute, Anreise und Abreise nicht bestimmen.',
      go: { label: 'Apartment öffnen', propertyId: booking.propertyId },
      urgent: true,
    };
  }
  if (code === 'InvalidStayRange') {
    return {
      title: 'Zeitraum der Buchung passt nicht zusammen',
      sub: `Anreise, Abreise oder tatsächliche Abreise widersprechen sich.${booking.source === 'Ota' ? ' Beim Kanal prüfen.' : ' Zeitraum über „Bearbeiten“ prüfen.'}`,
      go: null,
      urgent: true,
    };
  }
  return {
    title: 'Buchungsdaten prüfen',
    sub: 'Der Stand des Aufenthalts lässt sich gerade nicht bestimmen.',
    go: null,
    urgent: true,
  };
}

const LIVE = new Set(['Pending', 'Confirmed', 'CheckedIn', 'CheckedOut']);

/**
 * „Zu erledigen“ im Überblick – nur aus dem, was Server und Abfragen wirklich sagen. Unbekannt
 * (`null`, z. B. noch nicht geladen) erzeugt keine Aufgabe, statt eine zu behaupten.
 */
export function overviewTodos(
  booking: BookingResponse,
  onboardingComplete: boolean | null,
  invoiceState: InvoiceStepState | null,
): OverviewTodo[] {
  if (!LIVE.has(booking.status)) return [];
  const todos: OverviewTodo[] = [];
  if (booking.stayPhase === 'DataQuality') todos.push(dataQualityTodo(booking));
  if (onboardingComplete === false && booking.stayPhase !== 'Completed') {
    todos.push({
      title: 'Gast hat das Onboarding noch nicht abgeschlossen',
      sub: `Anreise ${stayDate(booking.checkInDate, booking.objectToday)}`,
      go: { label: 'Onboarding ansehen', tab: 'guest' },
      urgent: true,
    });
  }
  if (invoiceState === 'open') {
    todos.push({
      title: 'Rechnung noch nicht bezahlt',
      sub: 'Mindestens eine aktive Rechnung ist offen.',
      go: { label: 'Zu Geld', tab: 'billing' },
      urgent: true,
    });
  }
  // Kanal- und Stripe-Buchungen bekommen die Rechnung zur Abreise automatisch; eine Aufgabe ist
  // es erst, wenn der Aufenthalt vorbei ist und der Server das Erzeugen hier anbietet.
  if (
    invoiceState === 'none' &&
    booking.stayPhase === 'Completed' &&
    booking.capabilities?.canCreateAccommodationInvoice === true
  ) {
    todos.push({
      title: 'Noch keine Rechnung',
      sub: 'Lässt sich unter Geld erzeugen.',
      go: { label: 'Zu Geld', tab: 'billing' },
      urgent: false,
    });
  }
  return todos;
}

/** Zahl am Reiter = offene Aufgaben, die dorthin führen (Board „Buchung“), nicht die Zahl der Belege. */
export function todoCounts(todos: OverviewTodo[]): Partial<Record<BookingDetailTab, number>> {
  const counts: Partial<Record<BookingDetailTab, number>> = {};
  for (const todo of todos) {
    if (todo.go && 'tab' in todo.go) counts[todo.go.tab] = (counts[todo.go.tab] ?? 0) + 1;
  }
  return counts;
}
